import { useState, useEffect } from 'react'
import apiClient from '../services/api'
import type { NavCategory } from '../types/nav.types'

// In-memory cache so we don't re-fetch on every route change
let cachedCategories: NavCategory[] | null = null
let cacheTimestamp = 0
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

interface UseNavCategoriesState {
  categories: NavCategory[]
  loading: boolean
  error: boolean
}

export function useNavCategories(): UseNavCategoriesState {
  const [state, setState] = useState<UseNavCategoriesState>({
    categories: cachedCategories ?? [],
    loading: !cachedCategories,
    error: false,
  })

  useEffect(() => {
    const now = Date.now()
    if (cachedCategories && now - cacheTimestamp < CACHE_TTL) {
      setState({ categories: cachedCategories, loading: false, error: false })
      return
    }

    let cancelled = false

    apiClient
      .get<NavCategory[]>('/categories/tree/', { params: { tier: 1, include_children: true } })
      .then((r) => {
        if (!cancelled) {
          cachedCategories = r.data
          cacheTimestamp = Date.now()
          setState({ categories: r.data, loading: false, error: false })
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState((prev) => ({ ...prev, loading: false, error: true }))
        }
      })

    return () => { cancelled = true }
  }, [])

  return state
}